import { Link } from 'react-router-dom';
import SEO from '../../../components/base/SEO';

export default function DoubleWallChimneyPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: 'Double Wall Chimney Systems',
    description: 'High-performance double wall chimney systems designed for maximum insulation and safety. Perfect for residential and commercial applications requiring superior heat retention and protection.',
    brand: {
      '@type': 'Brand',
      name: 'Proform Chimney Systems'
    },
    manufacturer: {
      '@type': 'Organization',
      name: 'Proform Chimney Systems',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Akçaburgaz Mah. 3028 Sk. No:1 İç Kapı No:27',
        addressLocality: 'Esenyurt',
        addressRegion: 'Istanbul',
        addressCountry: 'TR'
      }
    },
    offers: {
      '@type': 'Offer',
      availability: 'https://schema.org/InStock',
      priceCurrency: 'TRY',
      url: `${siteUrl}/services/double-wall`
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.8',
      reviewCount: '127'
    }
  };

  const technicalSpecs = [
    { label: 'Inner Wall Material', value: '316L Stainless Steel' },
    { label: 'Outer Wall Material', value: '304 Stainless Steel' },
    { label: 'Insulation Material', value: 'Rock Wool' },
    { label: 'Insulation Thickness', value: '30mm / 50mm' },
    { label: 'Manufacturing Method', value: 'Laser Cut + TIG Welding' },
    { label: 'Connection Type', value: 'External Clamps' },
    { label: 'Temperature Resistance', value: 'Up to 600°C' },
    { label: 'Application', value: 'Residential & Commercial' }
  ];

  const images = [
    {
      url: 'https://static.readdy.ai/image/260cb4a7e92398667a2a2f5c835f3622/49ef0d2acfac0c7c126654f46f4774af.jpeg',
      title: 'Double Wall Chimney Products'
    },
    {
      url: 'https://static.readdy.ai/image/260cb4a7e92398667a2a2f5c835f3622/49ef0d2acfac0c7c126654f46f4774af.jpeg',
      title: 'Double Wall System Components'
    }
  ];

  return (
    <>
      <SEO
        title="Double Wall Chimney Systems Turkey - Premium Insulated Chimneys | Proform"
        description="High-performance double wall chimney systems with 316L stainless steel and superior insulation. Expert manufacturing and installation in Turkey for residential and commercial use."
        keywords="double wall chimney, insulated chimney Turkey, stainless steel chimney, residential chimney, commercial chimney installation"
        canonical={`${siteUrl}/services/double-wall`}
        schema={schema}
      />
      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="relative h-[500px] overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://proformbaca.com/wp-content/uploads/2024/02/1-scaled.jpg"
              alt="Double Wall Chimney Systems"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#1e3a8a]/95 via-[#1e40af]/90 to-[#1e3a8a]/95"></div>
          </div>
          <div className="relative max-w-7xl mx-auto px-6 h-full flex flex-col justify-center text-white">
            <nav className="mb-6">
              <ol className="flex items-center gap-2 text-sm">
                <li>
                  <Link to="/" className="hover:text-orange-400 transition-colors">Home</Link>
                </li>
                <li><i className="ri-arrow-right-s-line"></i></li>
                <li>
                  <Link to="/services" className="hover:text-orange-400 transition-colors">Services</Link>
                </li>
                <li><i className="ri-arrow-right-s-line"></i></li>
                <li className="text-orange-400 font-semibold">Double Wall</li>
              </ol>
            </nav>
            <h1 className="text-6xl md:text-7xl font-bold mb-6 leading-tight">Double Wall Chimney Systems</h1>
            <p className="text-2xl md:text-3xl max-w-3xl leading-relaxed">
              High-performance systems designed for maximum insulation and safety
            </p>
          </div>
        </section>

        {/* Description Section */}
        <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="bg-white rounded-3xl shadow-2xl p-12 mb-12">
              <div className="flex items-center gap-4 mb-8">
                <div className="w-16 h-16 bg-gradient-to-br from-[#1e3a8a] to-[#1e40af] rounded-2xl flex items-center justify-center shadow-lg">
                  <i className="ri-information-line text-3xl text-white"></i>
                </div>
                <h2 className="text-4xl font-bold text-gray-900">Product Description</h2>
              </div>
              <div className="space-y-6 text-gray-700 leading-relaxed text-lg">
                <p>
                  It is a waste gas discharge system consisting of double-walled modules, the inner walls of which are 316 L stainless steel and the outer walls of which we use 304 quality stainless steel sheet.
                </p>
                <p>
                  Since we use 30 mm or 50 mm thick rock wool as insulation material between the outer and inner walls, the flue gas temperature passing through the chimney is transmitted to the outer surface at a minimum level. For this reason, the condensation amount of the system is low and the chimney efficiency and fuel savings are high.
                </p>
                <p>
                  The stainless steel sheets we use in our chimney system modules are cut in laser cutting machines in our standard sizes or in customer-specific sizes according to the project and assembled by precision welding with our state-of-the-art Tig machine.
                </p>
                <p>
                  Thanks to the external connection clamps used at the joints, sealing and maximum connection strength are ensured, and the system installation is completed by our experienced installation staff.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Product Gallery - 2 Column Grid */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Product Gallery</h2>
              <p className="text-xl text-gray-600">Explore our double wall chimney systems in detail</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {images.map((image, index) => (
                <div key={index} className="relative group overflow-hidden rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500">
                  <div className="w-full h-96">
                    <img
                      src={image.url}
                      alt={image.title}
                      className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-6 text-white transform translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                    <h3 className="text-2xl font-bold">{image.title}</h3>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Technical Specifications Table */}
        <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-5xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Technical Specifications</h2>
              <p className="text-xl text-gray-600">Detailed specifications of our double wall systems</p>
            </div>
            <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gradient-to-r from-[#1e3a8a] to-[#1e40af]">
                      <th className="px-8 py-6 text-left text-xl font-bold text-white">Specification</th>
                      <th className="px-8 py-6 text-left text-xl font-bold text-white">Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {technicalSpecs.map((spec, index) => (
                      <tr key={index} className={`${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'} hover:bg-orange-50 transition-colors`}>
                        <td className="px-8 py-5 text-gray-900 font-semibold text-lg border-b border-gray-200">{spec.label}</td>
                        <td className="px-8 py-5 text-gray-700 text-lg border-b border-gray-200">{spec.value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-4xl font-bold text-center text-gray-900 mb-12">Key Features & Benefits</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-8 hover:shadow-xl transition-all duration-300 border-2 border-blue-200">
                <div className="w-16 h-16 bg-gradient-to-br from-[#1e3a8a] to-[#1e40af] rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                  <i className="ri-shield-check-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Superior Insulation</h3>
                <p className="text-gray-700 leading-relaxed">30-50mm rock wool insulation provides maximum heat retention and energy efficiency</p>
              </div>

              <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-2xl p-8 hover:shadow-xl transition-all duration-300 border-2 border-orange-200">
                <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                  <i className="ri-tools-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Premium Materials</h3>
                <p className="text-gray-700 leading-relaxed">316L inner wall and 304 stainless steel outer wall for durability and longevity</p>
              </div>

              <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-2xl p-8 hover:shadow-xl transition-all duration-300 border-2 border-teal-200">
                <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                  <i className="ri-fire-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Low Condensation</h3>
                <p className="text-gray-700 leading-relaxed">Minimal heat loss ensures reduced condensation and improved system efficiency</p>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl p-8 hover:shadow-xl transition-all duration-300 border-2 border-purple-200">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                  <i className="ri-scissors-cut-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Precision Manufacturing</h3>
                <p className="text-gray-700 leading-relaxed">Laser-cut components and TIG welding ensure perfect fit and maximum strength</p>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-8 hover:shadow-xl transition-all duration-300 border-2 border-green-200">
                <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                  <i className="ri-lock-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Secure Connections</h3>
                <p className="text-gray-700 leading-relaxed">External connection clamps provide superior sealing and connection strength</p>
              </div>

              <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-2xl p-8 hover:shadow-xl transition-all duration-300 border-2 border-red-200">
                <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-red-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                  <i className="ri-user-star-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Expert Installation</h3>
                <p className="text-gray-700 leading-relaxed">Professional installation by experienced staff ensures optimal performance</p>
              </div>
            </div>
          </div>
        </section>

        {/* Catalog Download Section */}
        <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-5xl mx-auto px-6">
            <div className="bg-gradient-to-br from-[#1e3a8a] to-[#1e40af] rounded-3xl shadow-2xl overflow-hidden">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
                <div className="p-12 flex flex-col justify-center text-white">
                  <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mb-6">
                    <i className="ri-file-pdf-2-line text-4xl text-white"></i>
                  </div>
                  <h2 className="text-3xl font-bold mb-4">Download Product Catalog</h2>
                  <p className="text-white/85 text-lg leading-relaxed mb-8">
                    Download our comprehensive catalog containing all technical specifications, dimensions, and installation details of our double wall chimney systems for free.
                  </p>
                  <a
                    href="https://drive.google.com/file/d/1v2PZL2A1kedHfq8XUtj9Utfjkspg7PqJ/view?usp=sharing"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-3 bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-xl text-lg font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg whitespace-nowrap cursor-pointer w-fit"
                  >
                    <i className="ri-download-2-line text-2xl"></i>
                    Download Catalog (PDF)
                  </a>
                </div>
                <div className="p-12 flex flex-col justify-center">
                  <h3 className="text-xl font-bold text-white mb-6">Catalog Contents</h3>
                  <ul className="space-y-4">
                    {[
                      'Technical drawings of double wall chimney systems',
                      'Material specifications and standards',
                      'Dimension tables and connection details',
                      'Installation guide and diagrams',
                      'Certificates and test reports'
                    ].map((item, i) => (
                      <li key={i} className="flex items-start gap-3 text-white/90">
                        <i className="ri-check-double-line text-orange-400 text-xl flex-shrink-0 mt-0.5"></i>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Prominent CTA Section */}
        <section className="py-24 bg-gradient-to-r from-orange-500 via-orange-600 to-orange-500 relative overflow-hidden">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
          </div>
          <div className="relative max-w-5xl mx-auto px-6 text-center">
            <div className="mb-8">
              <div className="inline-flex items-center gap-3 bg-white/20 backdrop-blur-sm px-6 py-3 rounded-full text-white font-semibold mb-6">
                <i className="ri-phone-line text-xl"></i>
                <span>Get in Touch Today</span>
              </div>
            </div>
            <h2 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">Ready to Get Started?</h2>
            <p className="text-2xl text-white/95 mb-12 leading-relaxed max-w-3xl mx-auto">
              Contact us today to discuss your double wall chimney system requirements and receive a free consultation
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
              <Link
                to="/contact"
                className="inline-flex items-center gap-3 bg-white text-orange-500 px-10 py-5 rounded-full font-bold text-xl hover:bg-gray-100 transition-all duration-300 shadow-2xl hover:shadow-3xl transform hover:scale-105 whitespace-nowrap"
              >
                <i className="ri-mail-line text-2xl"></i>
                <span>Contact Us</span>
              </Link>
              <a
                href="https://wa.me/905326304227"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 bg-[#25D366] text-white px-10 py-5 rounded-full font-bold text-xl hover:bg-[#20BA5A] transition-all duration-300 shadow-2xl hover:shadow-3xl transform hover:scale-105 whitespace-nowrap"
              >
                <i className="ri-whatsapp-line text-2xl"></i>
                <span>WhatsApp</span>
              </a>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}